import { useCallback } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import { CLICK_IDS } from '@sunflower-games/analytics-events';

import type { WizardChildProps } from '../../../components/Wizard/types';
import { analytics } from '../../../services/analytics';
import { auth, type AuthParams } from '../../../services/auth';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import { useHandleAuth } from '../useHandleAuth';

import { DirectEmailLandingOrder, DirectSignUpLandingOrder, SignupWizardOrder, SignUpWizardSteps } from './constants';
import { useSignUpWizardContext } from './signUpWizard.provider';

type Params =
  | (Partial<AuthParams> & {
      componentType?: 'dialog' | 'portal';
      areAllOptionalConsentsChecked?: boolean;
      eventClickId?: string;
      wizardProps?: Partial<WizardChildProps>;
    })
  | null
  | undefined;

export const useHandleSignUpWizard = () => {
  const { authStore, wizardStore, portalStore, uiStore, liveGeoStore } = useStores();

  const { step, nextStep, prevStep } = wizardStore;
  const { countryCode } = liveGeoStore.rawGeo ?? {};

  const { updateContextData, provider } = useSignUpWizardContext();

  const { handleAuth } = useHandleAuth((error) => {
    updateContextData({ error });
  }, nextStep);

  const isDirectSignUpLanding = authStore.isDirectSignUpLanding();
  const isDirectEmailLanding = authStore.isDirectEmailLanding();
  const isDirectRegLanding = authStore.isDirectRegLanding();

  const signUpProvider = provider || auth.getAuthParams()?.provider;

  const responsible_gaming_dialog = useFlag('responsible_gaming_dialog', ['']);
  const shouldShowRSGDialog = responsible_gaming_dialog.includes(countryCode || 'US');

  const handleSignUpAndEvents = useCallback(
    async (params: Params) => {
      const eventProvider = params?.provider;
      // Email/default sign-up is split into "select email" (SignUpOptions) and this "insert email" submit step;
      // other providers sign up directly, so they keep their provider name as the value.
      const isNonEmailProvider = eventProvider && eventProvider !== 'email';
      const eventParams: AnalyticsEvents['create_account'] = {
        event_category: 'sign_up',
        event_action: 'click',
        event_type: 'signup_platform',
        event_value: isNonEmailProvider ? eventProvider : 'insert_email',
      };

      void analytics.sendEvent('create_account', eventParams);
      updateContextData({ loading: true });
      try {
        return await handleAuth({
          provider: params?.provider || 'email',
          action: 'sign_up',
          credentials: params?.credentials,
          nextStep: params?.wizardProps?.nextStep || nextStep,
          ...params?.wizardProps,
        });
      } finally {
        updateContextData({ loading: false });
      }
    },
    [handleAuth, nextStep, updateContextData],
  );

  const handleSULanding = useCallback(
    (params: Params) => {
      const stepName = DirectSignUpLandingOrder[step];
      switch (stepName) {
        case SignUpWizardSteps.SignUpOptions: {
          const { areAllOptionalConsentsChecked } = params ?? {};
          const shouldNavigateToRSGDialog = !areAllOptionalConsentsChecked && shouldShowRSGDialog;
          const providerValue = params?.provider || signUpProvider;

          if (!providerValue) {
            throw new Error('Provider is not set');
          }

          if (shouldNavigateToRSGDialog) {
            nextStep();
            return;
          }
          if (providerValue === 'email') {
            nextStep();
          } else {
            void handleSignUpAndEvents({
              provider: providerValue,
              action: 'sign_up',
              credentials: params?.credentials,
              eventClickId: CLICK_IDS.AUTH_BUTTONS.SIGN_UP[providerValue],
            });
          }
          break;
        }
        case SignUpWizardSteps.ResponsibleSocialPlay:
          {
            if (!provider) {
              throw new Error('Provider is not set');
            }

            if (provider === 'email') {
              nextStep();
            } else {
              void handleSignUpAndEvents({
                provider: provider,
                action: 'sign_up',
                credentials: params?.credentials,
                eventClickId: CLICK_IDS.AUTH_BUTTONS['SIGN_UP'][provider],
              });
            }
          }
          break;
        case SignUpWizardSteps.SignUpRegister:
          if (!provider) {
            throw new Error('Provider is not set');
          }
          if (provider === 'email') {
            void handleSignUpAndEvents({
              provider: provider,
              action: 'sign_up',
              credentials: params?.credentials,
              eventClickId: CLICK_IDS.AUTH_BUTTONS['SIGN_UP'][provider],
            });
          } else {
            nextStep();
          }
          break;
        case SignUpWizardSteps.EmailVerification:
          nextStep();
          break;
        case SignUpWizardSteps.VerificationSuccess:
          nextStep();
          break;
        case SignUpWizardSteps.WelcomeBonusDialog:
          nextStep();
          break;
        case SignUpWizardSteps.ChooseCountry: {
          if (params?.componentType === 'portal') {
            portalStore.closePortal();
          } else {
            uiStore.setOpenDialog({ dialog: null });
          }
        }
      }
    },
    [step, nextStep, shouldShowRSGDialog, signUpProvider, handleSignUpAndEvents, provider, portalStore, uiStore],
  );

  const handleEMLanding = useCallback(
    (params: Params) => {
      const stepName = DirectEmailLandingOrder[step];
      switch (stepName) {
        case SignUpWizardSteps.SignUpEmailLanding: {
          const { areAllOptionalConsentsChecked } = params ?? {};
          const shouldNavigateToRSGDialog = !areAllOptionalConsentsChecked && shouldShowRSGDialog;
          const providerValue = params?.provider || signUpProvider;

          if (!providerValue) {
            throw new Error('Provider is not set');
          }

          if (shouldNavigateToRSGDialog) {
            nextStep();
            return;
          }
          void handleSignUpAndEvents({
            provider: providerValue,
            action: 'sign_up',
            credentials: params?.credentials,
            eventClickId: CLICK_IDS.AUTH_BUTTONS.SIGN_UP.email,
          });
          break;
        }
        case SignUpWizardSteps.ResponsibleSocialPlay:
          {
            if (!provider) {
              throw new Error('Provider is not set');
            }

            void handleSignUpAndEvents({
              provider: provider,
              action: 'sign_up',
              credentials: params?.credentials,
              eventClickId: CLICK_IDS.AUTH_BUTTONS['SIGN_UP'][provider],
            }).then(({ success }) => {
              if (!success) {
                prevStep();
              }
            });
          }
          break;
        case SignUpWizardSteps.EmailVerification:
          nextStep();
          break;
        case SignUpWizardSteps.VerificationSuccess:
          nextStep();
          break;
        case SignUpWizardSteps.WelcomeBonusDialog:
          nextStep();
          break;
        case SignUpWizardSteps.ChooseCountry: {
          if (params?.componentType === 'portal') {
            portalStore.closePortal();
          } else {
            uiStore.setOpenDialog({ dialog: null });
          }
        }
      }
    },
    [
      step,
      nextStep,
      shouldShowRSGDialog,
      signUpProvider,
      handleSignUpAndEvents,
      provider,
      prevStep,
      portalStore,
      uiStore,
    ],
  );

  const handleRegularLanding = useCallback(
    (params: Params) => {
      const stepName = SignupWizardOrder[step];
      switch (stepName) {
        case SignUpWizardSteps.SignUpPrivacy:
          nextStep();
          break;
        case SignUpWizardSteps.ResponsibleSocialPlay:
          nextStep();
          break;
        case SignUpWizardSteps.SignUpOptions: {
          const providerValue = params?.provider || signUpProvider;
          if (!providerValue) {
            throw new Error('Provider is not set');
          }

          if (providerValue === 'email') {
            nextStep();
          } else {
            void handleSignUpAndEvents({
              provider: providerValue,
              action: 'sign_up',
              credentials: params?.credentials,
              eventClickId: CLICK_IDS.AUTH_BUTTONS['SIGN_UP'][providerValue],
            });
          }
          break;
        }
        case SignUpWizardSteps.SignUpRegister: {
          const { email, password } = params?.credentials || {};
          if (!email || !password) {
            return;
          }
          void handleSignUpAndEvents({
            provider: 'email',
            action: 'sign_up',
            credentials: params?.credentials,
            eventClickId: CLICK_IDS.AUTH_FLOW.CREATE_EMAIL_ACCOUNT,
          });
          break;
        }
        case SignUpWizardSteps.EmailVerification: {
          nextStep();
          break;
        }
        case SignUpWizardSteps.VerificationSuccess: {
          nextStep();
          break;
        }
        case SignUpWizardSteps.WelcomeBonusDialog: {
          nextStep();
          break;
        }
        case SignUpWizardSteps.ChooseCountry: {
          if (params?.componentType === 'portal') {
            portalStore.closePortal();
          } else {
            uiStore.setOpenDialog({ dialog: null });
          }
        }
      }
    },
    [handleSignUpAndEvents, nextStep, portalStore, signUpProvider, step, uiStore],
  );

  const handleRegLanding = useCallback(
    (params: Params) => {
      void handleSignUpAndEvents({
        provider: params?.provider || 'email',
        action: 'sign_up',
        credentials: params?.credentials,
        eventClickId: CLICK_IDS.AUTH_FLOW.CREATE_EMAIL_ACCOUNT,
        wizardProps: params?.wizardProps,
      });
    },
    [handleSignUpAndEvents],
  );

  const handleSignUpWizard = useCallback(
    (params?: Params | null) => {
      if (params?.provider) {
        updateContextData({
          provider: params.provider,
          credentials: params?.credentials,
        });
      }
      if (isDirectSignUpLanding) {
        return handleSULanding(params);
      }
      if (isDirectEmailLanding) {
        return handleEMLanding(params);
      }
      if (isDirectRegLanding) {
        return handleRegLanding(params);
      }
      return handleRegularLanding(params);
    },
    [
      isDirectSignUpLanding,
      isDirectEmailLanding,
      isDirectRegLanding,
      handleRegularLanding,
      updateContextData,
      handleSULanding,
      handleEMLanding,
      handleRegLanding,
    ],
  );

  return { handleSignUpWizard };
};
